import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

// True flight length for a flight itinerary item.
//
// Source of truth, in priority order:
//   1. The flight provider's absolute take-off/landing instants, persisted on
//      the leg by "Use this flight" (`details.flightDepartureUtc` /
//      `flightArrivalUtc`). Plain instant subtraction = exact gate-to-gate,
//      DST- and multi-day-correct, no reconstruction. Used only when still
//      consistent with the displayed take-off/landing times (a later manual
//      time edit invalidates them → we fall through).
//   2. Reconstruction from the leg's take-off/landing wall-clock times +
//      airport timezones (manual edits, where no provider instants exist).
//      Landing earlier in the day than take-off rolls one day forward.
//   3. Raw wall-clock span when timezones are missing.
//
// The item's start/end date columns are never used — they can drift days apart
// on a stale reschedule and would inflate the duration.

type TimeOfDay = { hours?: number | null; minutes?: number | null };

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const toMinutes = (v: unknown): number | null => {
  if (!isObj(v)) return null;
  const { hours, minutes } = v as TimeOfDay;
  const h = typeof hours === 'number' ? hours : 0;
  const m = typeof minutes === 'number' ? minutes : 0;
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const tzOf = (loc: unknown): string | null =>
  isObj(loc) && typeof loc.timezone === 'string' && loc.timezone
    ? loc.timezone
    : null;

const strOf = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

const readFlightLeg = (
  legs: unknown
): {
  startMin: number;
  endMin: number;
  startTz: string | null;
  endTz: string | null;
  departureUtc: string | null;
  arrivalUtc: string | null;
} | null => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const first = legs[0];
  const details = isObj(first) ? first.details : null;
  if (!isObj(details)) return null;
  const startMin = toMinutes(details.startTime);
  const endMin = toMinutes(details.endTime);
  if (startMin === null || endMin === null) return null;
  return {
    startMin,
    endMin,
    startTz: tzOf(details.startLocation),
    endTz: tzOf(details.endLocation),
    departureUtc: strOf(details.flightDepartureUtc),
    arrivalUtc: strOf(details.flightArrivalUtc),
  };
};

// Exact duration from the provider's absolute instants, but only if they still
// describe the displayed take-off/landing times (so a manual time edit, which
// changes the wall-clock but not these instants, correctly falls back to
// reconstruction). Returns null when unavailable/stale.
const durationFromProviderInstants = (leg: {
  startMin: number;
  endMin: number;
  startTz: string | null;
  endTz: string | null;
  departureUtc: string | null;
  arrivalUtc: string | null;
}): number | null => {
  if (!leg.departureUtc || !leg.arrivalUtc || !leg.startTz || !leg.endTz) {
    return null;
  }
  const dep = new Date(leg.departureUtc);
  const arr = new Date(leg.arrivalUtc);
  if (Number.isNaN(dep.getTime()) || Number.isNaN(arr.getTime())) return null;
  // Consistency check: the instants' local clock times must match the
  // displayed take-off/landing, else they're stale (user edited the times).
  if (
    formatInTimeZone(dep, leg.startTz, 'HH:mm') !== hhmm(leg.startMin) ||
    formatInTimeZone(arr, leg.endTz, 'HH:mm') !== hhmm(leg.endMin)
  ) {
    return null;
  }
  const minutes = Math.round((arr.getTime() - dep.getTime()) / 60_000);
  return minutes > 0 && minutes <= 48 * 60 ? minutes : null;
};

const pad2 = (n: number): string => String(n).padStart(2, '0');
const hhmm = (min: number): string =>
  `${pad2(Math.floor(min / 60))}:${pad2(min % 60)}`;

export const flightDurationMinutes = (item: {
  startTime: string | null;
  legs?: unknown;
}): number | null => {
  const leg = readFlightLeg(item.legs);
  if (!leg) return null;

  // 1. Provider's absolute instants — exact, when present and consistent.
  const fromProvider = durationFromProviderInstants(leg);
  if (fromProvider !== null) return fromProvider;

  // 2/3. Reconstruct from the displayed take-off/landing times.
  const rollover = leg.endMin < leg.startMin;
  const wallSpan = rollover
    ? 24 * 60 - leg.startMin + leg.endMin
    : leg.endMin - leg.startMin;

  const m = item.startTime?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (leg.startTz && leg.endTz && m) {
    const depDate = `${m[1]}-${m[2]}-${m[3]}`;
    const arr = new Date(
      Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) +
        (rollover ? 86_400_000 : 0)
    );
    const arrDate = arr.toISOString().slice(0, 10);
    const dep = fromZonedTime(
      `${depDate}T${hhmm(leg.startMin)}:00`,
      leg.startTz
    );
    const land = fromZonedTime(`${arrDate}T${hhmm(leg.endMin)}:00`, leg.endTz);
    const minutes = Math.round((land.getTime() - dep.getTime()) / 60_000);
    if (Number.isFinite(minutes) && minutes > 0 && minutes <= 48 * 60) {
      return minutes;
    }
  }
  return wallSpan > 0 ? wallSpan : null;
};

// Flight-only convenience: the "Xh Ym" label for an item, or null when the
// item isn't a flight or has no derivable flight length. Lets every duration
// surface (cards, overview, PDF/PPTX/calendar exports) share one source of
// truth — `item.contentSubcategory === 'Flight' ? formatFlightDuration(item)
// : <generic duration>`.
export const formatFlightDuration = (item: {
  contentSubcategory?: string | null;
  startTime: string | null;
  legs?: unknown;
}): string | null => {
  if (item.contentSubcategory !== 'Flight') return null;
  const minutes = flightDurationMinutes(item);
  if (minutes === null) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const label = [h > 0 ? `${h}h` : '', m > 0 ? `${m}m` : '']
    .filter(Boolean)
    .join(' ');
  return label || null;
};
